import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Command, ShieldCheck, Loader2 } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isRegister) {
        const r = await register(email, password, name);
        if (r?.connect_onboarding_url) {
          toast.success("Command Center provisioned — routing to Stripe onboarding…");
          window.location.href = r.connect_onboarding_url;
          return;
        }
      } else {
        await login(email, password);
      }
      toast.success(isRegister ? "Command Center provisioned." : "Welcome back, Commander.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex uc-grain" data-testid="auth-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative"
           style={{ background: "linear-gradient(180deg, #0A0A0A 0%, #101010 100%)", borderRight: "1px solid var(--uc-border)" }}>
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-9 h-9 flex items-center justify-center border border-white/15" style={{ background: "var(--uc-surface-2)" }}>
            <Command size={18} className="text-amber-400" />
          </div>
          <span className="font-mono-uc text-xs uppercase tracking-[0.25em] text-white/70">Umbrella Command OS</span>
        </div>
        <div className="relative z-10 max-w-lg">
          <div className="uc-chip mb-6"><span className="uc-dot bg-amber-400"></span> WEALTH INTELLIGENCE</div>
          <h1 className="font-serif-display text-6xl leading-[1.05] mb-6">
            One control tower<br/>for every venture<br/><span className="italic text-amber-400/90">you command.</span>
          </h1>
          <p className="text-white/60 text-base max-w-md leading-relaxed">
            The Pulse aggregates ten ventures in real time. Scout hunts opportunity, Sentinel guards the flank, and Executor moves — under budget, on your terms.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4 relative z-10">
          {[
            {k: "Net Worth Pulse", v: "Live"},
            {k: "Agent Boardroom", v: "7 Agents"},
            {k: "Private Vault", v: "Encrypted"},
          ].map((f) => (
            <div key={f.k} className="uc-card p-4">
              <div className="text-[10px] font-mono-uc uppercase tracking-wider text-white/40">{f.k}</div>
              <div className="font-serif-display text-2xl mt-1">{f.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <div className="w-9 h-9 flex items-center justify-center border border-white/15" style={{ background: "var(--uc-surface-2)" }}>
              <Command size={18} className="text-amber-400" />
            </div>
            <span className="font-mono-uc text-xs uppercase tracking-[0.25em] text-white/70">Umbrella Command OS</span>
          </div>

          <div>
            <div className="uc-chip mb-4"><ShieldCheck size={11} /> SECURE ACCESS</div>
            {!isRegister && typeof window !== "undefined" &&
              new URLSearchParams(window.location.search).get("expired") === "1" && (
              <div className="border border-rose-500/40 bg-rose-500/[0.06] px-3 py-2.5 mb-4 flex items-center gap-2"
                   data-testid="session-expired-banner">
                <span className="uc-dot bg-rose-400"></span>
                <span className="font-mono-uc text-[10px] uppercase tracking-widest text-rose-300">
                  Session Expired — Re-Login Required
                </span>
                <span className="text-[10px] text-white/50">Your data is intact; the session lapsed.</span>
              </div>
            )}
            <h2 className="font-serif-display text-4xl mb-1">
              {isRegister ? "Provision your Command Center" : "Enter your Command Center"}
            </h2>
            <p className="text-white/50 text-sm">
              {isRegister ? "Set up your umbrella in under a minute." : "Sign in to resume operational awareness."}
            </p>
          </div>

          {isRegister && (
            <div className="space-y-2">
              <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Command Center Name</Label>
              <Input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Hawthorne Holdings OS"
                className="uc-focus bg-[#121212] border-white/10 h-11 rounded-none"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Email</Label>
            <Input
              data-testid="auth-email-input"
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="commander@empire.io"
              className="uc-focus bg-[#121212] border-white/10 h-11 rounded-none"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Password</Label>
            <Input
              data-testid="auth-password-input"
              type="password" required minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="uc-focus bg-[#121212] border-white/10 h-11 rounded-none"
            />
          </div>

          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full h-11 uc-btn-amber rounded-none uc-focus"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : isRegister ? "Provision Command Center" : "Enter Command Center"}
          </Button>

          <div className="text-sm text-white/50 text-center">
            {isRegister ? (
              <>Already provisioned? <Link data-testid="auth-switch-login" to="/login" className="text-amber-400 hover:underline">Sign in</Link></>
            ) : (
              <>No command center yet? <Link data-testid="auth-switch-register" to="/register" className="text-amber-400 hover:underline">Provision one</Link></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
